import { useState } from "react";
import { Modal } from "./common/Modal";

const STATUS_OPTIONS = ["OK", "NOT OK", "NA"];
// Frequency is chosen inside the sheet — per checkpoint, and for the sheet as a whole.
const FREQ_OPTIONS = ["6 MONTH", "1 YEAR"];

function printPMCheckSheet() {
  const old = document.getElementById("dynamic-pm-print-paper");
  if (old) old.remove();
  const styleEl = document.createElement("style");
  styleEl.id = "dynamic-pm-print-paper";
  styleEl.textContent = `@media print { @page { size: A4 landscape; margin: 8mm; } }`;
  document.head.appendChild(styleEl);
  const cleanup = () => {
    const s = document.getElementById("dynamic-pm-print-paper");
    if (s) s.remove();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  setTimeout(() => {
    window.print();
    setTimeout(cleanup, 5000);
  }, 100);
}

export default function PMCheckSheetFillModal({ template, plan, month, pmHalf, existingData, completedDate, completedBy, machine, sheetFreqOptions = [], onChangeSheetFreq, onSave, onClose }) {
  const [results, setResults] = useState(() => {
    const cps = template.checkpoints || [];
    const existing = (existingData && existingData.results) || [];
    return cps.map((cp, i) => {
      const old = existing[i];
      return {
        text: cp.text || "",
        // Specification: restore the previously-saved (possibly edited) value for this PM if present,
        // otherwise fall back to the master template's specification.
        specification: (old && old.specification != null) ? old.specification : (cp.specification || ""),
        checkingMethod: cp.checkingMethod || "",
        // Frequency: restore what was chosen for this row last time — without this the row
        // silently snapped back to the sheet's frequency every time the sheet was reopened,
        // throwing away any checkpoint marked 1 YEAR. Falls back to the checkpoint's own
        // frequency, then the sheet's.
        frequency: (old && old.frequency) || cp.freq || cp.frequency || template.frequency || "",
        problemFound: old?.problemFound || "",
        action: old?.action || "",
        status: old?.status || "NA",
      };
    });
  });
  const [err, setErr] = useState("");

  // AIR COMPRESSOR only: running-hours rows A, B, C. Total C = Last A - Today B (A - B = C).
  const isAirCompressor = (template.machineType || "").trim().toUpperCase() === "AIR COMPRESSOR";
  const [runningHours, setRunningHours] = useState(() => ({
    last: (existingData && existingData.runningHours && existingData.runningHours.last) || "",
    today: (existingData && existingData.runningHours && existingData.runningHours.today) || "",
  }));
  const _rhA = parseFloat(runningHours.last);
  const _rhB = parseFloat(runningHours.today);
  const totalRunningHrs = (!isNaN(_rhA) && !isNaN(_rhB)) ? (_rhB - _rhA) : "";

  function updateRow(i, field, value) {
    setResults(rows => rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  }

  // The other sheet has different checkpoints, so switching clears whatever is typed here.
  function switchSheet(next) {
    if (!onChangeSheetFreq || next === template.frequency) return;
    const touched = results.some(r =>
      (r.problemFound || "").trim() || (r.action || "").trim() || (r.status && r.status !== "NA"));
    if (touched && !window.confirm(
      `Switch to the ${next} check sheet?\n\nIt has different checkpoints, so anything filled in here will be cleared.`)) return;
    onChangeSheetFreq(next);
  }

  function handleSave() {
    setErr("");
    if (results.some(r => !r.status)) { setErr("Every checkpoint must have a Status (OK / NOT OK / NA)."); return; }
    onSave({
      templateId: template.id,
      templateDocNo: template.docNo || "",
      machineType: template.machineType || "",
      frequency: template.frequency || "",
      pmHalf: pmHalf || null,
      filledAt: new Date().toISOString(),
      runningHours: isAirCompressor ? {
        last: runningHours.last,
        today: runningHours.today,
        total: totalRunningHrs === "" ? "" : String(totalRunningHrs),
      } : undefined,
      results: results.map(r => ({
        text: r.text,
        frequency: r.frequency,
        specification: r.specification,
        problemFound: r.problemFound,
        action: r.action,
        status: r.status,
      })),
    });
  }

  return (
    <Modal title={`PM Check Sheet \u2014 ${template.machineType}`} onClose={onClose} size="wide">
      <div className="space-y-3 p-2 max-h-[80vh] overflow-y-auto pm-checksheet-printable">
        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 no-print">{err}</div>}

        <div className="hidden print:block text-center mb-3">
          <div className="font-bold text-lg">ADVANCED ANMOL METCOMP PVT. LTD.</div>
          <div className="font-semibold text-base">Machine Preventive Maintenance Check Sheet ({template.machineType})</div>
          <div className="text-xs">Doc No: {template.docNo || "\u2014"} &middot; Frequency: {template.frequency}</div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs space-y-0.5 print:bg-white">
          <div className="grid grid-cols-2 gap-2">
            <div><strong>Machine:</strong> {plan.machineCode} — {plan.desc}</div>
            <div><strong>Capacity:</strong> {machine?.capacity || "\u2014"}</div>
            <div><strong>Make/Model:</strong> {machine?.make || "\u2014"}</div>
            <div><strong>Doc No:</strong> {template.docNo || "\u2014"}</div>
            <div className="flex items-center gap-2">
              <strong>Frequency:</strong>
              {/* Switching sheet swaps the whole checkpoint list, so it lives here with the
                  sheet's own details rather than on the screen behind. */}
              {sheetFreqOptions.length > 1 && onChangeSheetFreq ? (
                <>
                  <select value={template.frequency} onChange={e => switchSheet(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-0.5 text-xs bg-white font-semibold print:hidden"
                    data-testid="checksheet-sheet-freq">
                    {sheetFreqOptions.map(fq => <option key={fq} value={fq}>{fq}</option>)}
                  </select>
                  <span className="hidden print:inline">{template.frequency}</span>
                </>
              ) : template.frequency}
            </div>
            <div><strong>Month:</strong> {month}{pmHalf ? ` (${pmHalf === 1 ? "1st PM" : "2nd PM"} of FY)` : ""}</div>
            <div><strong>P/M Performed Date:</strong> {completedDate || "\u2014"}</div>
            <div><strong>Performed By:</strong> {completedBy || "\u2014"}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border" style={{ minWidth: "1200px" }}>
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="border px-2 py-2 w-10">#</th>
                <th className="border px-2 py-2 text-left" style={{ minWidth: "160px" }}>Checkpoint</th>
                <th className="border px-2 py-2 text-left w-24">Frequency</th>
                <th className="border px-2 py-2 text-left" style={{ minWidth: "140px" }}>Specification</th>
                <th className="border px-2 py-2 text-left" style={{ minWidth: "120px" }}>Checking Method</th>
                <th className="border px-2 py-2 text-left" style={{ minWidth: "220px" }}>Problem Found</th>
                <th className="border px-2 py-2 text-left" style={{ minWidth: "220px" }}>Action</th>
                <th className="border px-2 py-2 w-28 text-left">Status After PM *</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className="border-b align-top">
                  <td className="border px-2 py-2 text-center text-gray-500">{i + 1}</td>
                  <td className="border px-2 py-2 font-medium">{r.text}</td>
                  <td className="border px-2 py-2">
                    <select value={FREQ_OPTIONS.includes(r.frequency) ? r.frequency : (FREQ_OPTIONS[0])}
                      onChange={e => updateRow(i, "frequency", e.target.value)}
                      className="w-full px-1 py-1 border rounded text-xs bg-white print:hidden" data-testid={`checksheet-freq-${i}`}>
                      {FREQ_OPTIONS.map(fq => <option key={fq} value={fq}>{fq}</option>)}
                    </select>
                    <div className="hidden print:block whitespace-nowrap">{r.frequency || "—"}</div>
                  </td>
                  <td className="border px-2 py-2">
                    <textarea value={r.specification} onChange={e => updateRow(i, "specification", e.target.value)} rows={2} placeholder="Specification..." className="w-full px-2 py-1 border rounded text-xs resize-y print:hidden" />
                    <div className="hidden print:block whitespace-pre-wrap">{r.specification || "\u2014"}</div>
                  </td>
                  <td className="border px-2 py-2 text-gray-600">{r.checkingMethod || "\u2014"}</td>
                  <td className="border px-2 py-2">
                    <textarea value={r.problemFound} onChange={e => updateRow(i, "problemFound", e.target.value)} rows={2} placeholder="Type any problem found during PM..." className="w-full px-2 py-1 border rounded text-xs resize-y print:hidden" />
                    <div className="hidden print:block whitespace-pre-wrap">{r.problemFound || "\u2014"}</div>
                  </td>
                  <td className="border px-2 py-2">
                    <textarea value={r.action} onChange={e => updateRow(i, "action", e.target.value)} rows={2} placeholder="Type action taken to resolve..." className="w-full px-2 py-1 border rounded text-xs resize-y print:hidden" />
                    <div className="hidden print:block whitespace-pre-wrap">{r.action || "\u2014"}</div>
                  </td>
                  <td className="border px-2 py-2">
                    <select value={r.status} onChange={e => updateRow(i, "status", e.target.value)} className="w-full px-2 py-1.5 border rounded text-xs bg-white font-semibold print:hidden">
                      {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
                    </select>
                    <div className="hidden print:block font-semibold">{r.status}</div>
                  </td>
                </tr>
              ))}
              {isAirCompressor && (
                <>
                  <tr className="border-b align-top">
                    <td className="border px-2 py-2 text-center font-bold text-gray-700">A</td>
                    <td className="border px-2 py-2 font-medium">Last Running Hrs</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"\u2014"}</td>
                    <td className="border px-2 py-2">
                      <input type="number" value={runningHours.last} onChange={e => setRunningHours(p => ({ ...p, last: e.target.value }))} placeholder="Last running hrs" className="w-full px-2 py-1 border rounded text-xs print:hidden" />
                      <div className="hidden print:block">{runningHours.last || "—"}</div>
                    </td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                  </tr>
                  <tr className="border-b align-top">
                    <td className="border px-2 py-2 text-center font-bold text-gray-700">B</td>
                    <td className="border px-2 py-2 font-medium">Today Running Hrs</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"\u2014"}</td>
                    <td className="border px-2 py-2">
                      <input type="number" value={runningHours.today} onChange={e => setRunningHours(p => ({ ...p, today: e.target.value }))} placeholder="Today running hrs" className="w-full px-2 py-1 border rounded text-xs print:hidden" />
                      <div className="hidden print:block">{runningHours.today || "—"}</div>
                    </td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                  </tr>
                  <tr className="border-b align-top bg-gray-50">
                    <td className="border px-2 py-2 text-center font-bold text-gray-700">C</td>
                    <td className="border px-2 py-2 font-medium">Total Running Hrs</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"\u2014"}</td>
                    <td className="border px-2 py-2 font-semibold">
                      {totalRunningHrs === "" ? <span className="text-gray-400">{"—"}</span> : totalRunningHrs}
                      <div className="text-[10px] text-gray-400 font-normal print:hidden">= Today − Last (auto)</div>
                    </td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                    <td className="border px-2 py-2 text-center text-gray-400">{"—"}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="hidden print:grid grid-cols-2 gap-3 mt-6 text-xs">
          <div>Performed By: <strong>{completedBy || "_____________________"}</strong> &nbsp;&nbsp; Sign: ___________</div>
          <div>Approved By: _____________________ &nbsp;&nbsp; Sign: ___________</div>
        </div>

        <div className="flex gap-2 pt-2 no-print">
          <button onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-blue-700" data-testid="checksheet-save-btn">Save Check Sheet</button>
          <button onClick={printPMCheckSheet} className="bg-indigo-600 text-white px-4 py-2 rounded text-sm font-semibold hover:bg-indigo-700" data-testid="checksheet-print-btn">Print</button>
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}
